import { Consult } from '@/app/services/consultService/getAllConsults'
import masks from '@/app/utils/masks'
import { BasicAvatar } from '@/view/components/BasicAvatar'
import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import {
  AlertCircle,
  CheckCircle,
  Clock,
  SquareArrowOutUpRight,
  Trash,
} from 'lucide-react'
import { Link } from 'react-router-dom'

type CustomerTableConsultsProps = {
  onDeleteClick?: (row: Consult) => void
}

const columnHelper = createColumnHelper<Consult>()

export const CustomerTableConsults = (
  onDeleteClick?: CustomerTableConsultsProps['onDeleteClick'],
) => {
  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => <div className="px-4 text-left">Cliente</div>,
      cell: ({ row }) => <div className="px-4">{row.original.name}</div>,
    }),
    columnHelper.accessor('cpf', {
      id: 'cpf',
      header: () => <div className="text-center">CPF</div>,
      cell: ({ row }) => (
        <div className="text-center">{masks.cpf(row.original.cpf)}</div>
      ),
    }),
    columnHelper.accessor('available', {
      id: 'available',
      header: () => <div className="text-center">Consulta</div>,
      cell: ({ row }) => {
        const { schedule, isAutorized, amount } = row.original.available

        return (
          <div className="flex justify-center">
            <div className="flex items-center gap-2 rounded-xl border border-gray01 px-3 py-1 text-sm">
              {schedule ? (
                <>
                  <Clock size={16} className="text-info" />
                  <span className="text-info">{masks.date(schedule)}</span>
                </>
              ) : isAutorized === false ? (
                <>
                  <AlertCircle size={16} className="text-error" />
                  <span className="text-error">Não autorizado</span>
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="text-success" />
                  <span>{masks.currency(amount ?? 0)}</span>
                </>
              )}
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('seller', {
      id: 'seller',
      header: () => <div className="text-center">Vendedor</div>,
      cell: ({ row }) => {
        const seller = row.original.seller

        if (!seller || !seller.name) {
          return (
            <div className="hidden justify-center lg:flex">
              <span className="italic text-gray-500">
                Vendedor não disponível
              </span>
            </div>
          )
        }

        return (
          <div className="hidden justify-center lg:flex">
            <div className="flex items-center gap-2">
              <BasicAvatar
                name={seller.name}
                profileImageUrl={seller.profileImageUrl}
              />
              <p>{seller.name}</p>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('lastTransaction', {
      id: 'lastTransaction',
      header: () => <div className="px-4 text-right">Último saque</div>,
      cell: ({ row }) => {
        const rowData = row.original
        const lastTransactionText = rowData.lastTransaction
          ? format(rowData.lastTransaction, 'dd/MM/yyyy, HH:mm')
          : 'Não possui saque'

        return (
          <div className="group flex items-center justify-end space-x-4 px-4 text-right">
            <span>{lastTransactionText}</span>
          </div>
        )
      },
    }),
    columnHelper.accessor('_id', {
      id: 'lastTransaction',
      header: () => <div></div>,
      cell: ({ row }) => {
        const rowData = row.original

        return (
          <div className="group flex items-center justify-end space-x-4 px-4 text-right">
            <Link to={`/clientes/${rowData._id}`}>
              <SquareArrowOutUpRight
                size={18}
                className="invisible cursor-pointer transition-opacity duration-200 group-hover:visible"
              />
            </Link>
            <Trash
              size={18}
              className="invisible cursor-pointer text-red-500 transition-opacity duration-200 group-hover:visible"
              onClick={() => onDeleteClick?.(rowData)}
            />
          </div>
        )
      },
    }),
  ]

  return { columns }
}
